import React, { useState } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stepper,
    Step,
    StepLabel,
    Stack,
    Typography,
    TextField,
    useTheme,
    alpha,
    Alert,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import DevicesIcon from "@mui/icons-material/Devices";
import DnsIcon from "@mui/icons-material/Dns";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { IconButton } from "@mui/material";
import { AppId } from "../../../../app/appTypes";
import { changeValidatedObject, ProcessStageOverview, ValidationObjectType } from "../validationProcessApi";
import { useAppDispatch } from "../../../../app/hooks";
import { DeviceSelectField } from "../../../device/DeviceSelectField";
import { ComputerizedSystemSelectField } from "../../../computerizedSystems/ComputerizedSystemSelectField";
import { showSuccess } from "../../../notifications/notificationsSlice";
import { Localized } from "@fluent/react";

type ChangeValidatedObjectProps = {
    open: boolean;
    processId: AppId;
    onClose: () => void;
    onSuccess: () => void;
};

const steps = ["Typ obiektu", "Wybór obiektu"];

const PROCESS_TYPE_LABEL: Record<ValidationObjectType, string> = {
    DEVICE: "Urządzenie",
    COMPUTERIZED_SYSTEM: "System skomputeryzowany",
    ROOM: "Pomieszczenie",
};

export const ChangeValidatedObjectWizard: React.FC<ChangeValidatedObjectProps> = ({
    open,
    processId,
    onClose,
    onSuccess,
}) => {
    const theme = useTheme();
    const dispatch = useAppDispatch();

    const [activeStep, setActiveStep] = useState(0);
    const [objectType, setObjectType] = useState<ValidationObjectType | null>(null);

    const [deviceId, setDeviceId] = useState<string | null>(null);
    const [systemId, setSystemId] = useState<string | null>(null);
    const [roomId, setRoomId] = useState<string | null>(null);

    const [saving, setSaving] = useState(false);
    const [serverError, setServerError] = useState("");

    const resetState = () => {
        setActiveStep(0);
        setObjectType(null);
        setDeviceId(null);
        setSystemId(null);
        setRoomId(null);
        setServerError("");
        setSaving(false);
    };

    const handleClose = () => {
        if (saving) return;
        resetState();
        onClose();
    };

    const canGoNext = () => {
        if (activeStep === 0) {
            return !!objectType;
        }
        if (activeStep === 1) {
            if (objectType === "DEVICE") return !!deviceId;
            if (objectType === "COMPUTERIZED_SYSTEM") return !!systemId;
            if (objectType === "ROOM") return !!roomId;
            return false;
        }
        return true;
    };

    const handleNext = () => {
        if (activeStep === 0 && objectType) {
            setActiveStep(1);
        }
    };

    const handleBack = () => {
        if (activeStep === 1) {
            setActiveStep(0);
        }
    };

    const handleSave = async () => {
        if (!objectType) return;

        let objectId: string | null = null;
        if (objectType === "DEVICE") objectId = deviceId;
        if (objectType === "COMPUTERIZED_SYSTEM") objectId = systemId;
        if (objectType === "ROOM") objectId = roomId;

        if (!objectId) {
            setServerError("Wybierz obiekt.");
            return;
        }

        setSaving(true);
        setServerError("");
        try {
            const result = await dispatch(
                changeValidatedObject({
                    processId,
                    objectType,
                    objectId,
                })
            )

            if (changeValidatedObject.fulfilled.match(result)) {
                const payload = result.payload as ProcessStageOverview;
                onSuccess();
                dispatch(showSuccess("saved"));

            } else {
                let errorMsg = "error";
                if (result.payload && result.payload.kind === "http") {
                    const problem = result.payload.problem;
                    if (problem) {
                        errorMsg = problem.title;
                    }
                }
                setServerError(errorMsg);
            }


        } catch (e: any) {
            setServerError(e?.message ?? "Nie udało się zmienić walidowanego obiektu.");
        } finally {
            setSaving(false);
        }
    };

    const cardStyle =
        (selected: boolean) =>
            (theme: any) => ({
                position: "relative",
                p: 1.5,
                border: 2,
                borderRadius: 2,
                cursor: "pointer",
                width: 180,
                textAlign: "center",
                transition:
                    "background-color .15s ease, border-color .15s ease, transform .05s ease",
                borderColor: selected ? theme.palette.success.main : theme.palette.divider,
                bgcolor: selected
                    ? alpha(theme.palette.success.main, 0.08)
                    : theme.palette.background.paper,
                "&:hover": {
                    borderColor: theme.palette.success.main,
                    bgcolor: selected
                        ? alpha(theme.palette.success.main, 0.12)
                        : theme.palette.action.hover,
                },
                "&:active": { transform: "scale(0.997)" },

                "& .card-label": {
                    fontWeight: 700,
                    color: theme.palette.text.primary,
                },

                "& .selected-mark": {
                    position: "absolute",
                    top: 6,
                    right: 6,
                    color: theme.palette.success.main,
                    fontSize: 22,
                    pointerEvents: "none",
                    display: selected ? "block" : "none",
                },
            });

    const renderStepContent = () => {
        if (activeStep === 0) {
            return (
                <Box>
                    <Typography variant="subtitle1" sx={{ mb: 2 }}>
                        Wybierz typ walidowanego obiektu
                    </Typography>
                    <Stack direction="row" spacing={2}>
                        <Box
                            onClick={() => setObjectType("DEVICE")}
                            sx={cardStyle(objectType === "DEVICE")}
                            role="button"
                            tabIndex={0}
                        >
                            <CheckCircleIcon className="selected-mark" />
                            <DevicesIcon />
                            <Typography className="card-label">Urządzenie</Typography>
                        </Box>
                        <Box
                            onClick={() => setObjectType("COMPUTERIZED_SYSTEM")}
                            sx={cardStyle(objectType === "COMPUTERIZED_SYSTEM")}
                            role="button"
                            tabIndex={0}
                        >
                            <CheckCircleIcon className="selected-mark" />
                            <DnsIcon />
                            <Typography className="card-label">System skomputeryzowany</Typography>
                        </Box>
                        <Box
                            onClick={() => setObjectType("ROOM")}
                            sx={cardStyle(objectType === "ROOM")}
                            role="button"
                            tabIndex={0}
                        >
                            <CheckCircleIcon className="selected-mark" />
                            <MeetingRoomIcon />
                            <Typography className="card-label">Pomieszczenie</Typography>
                        </Box>
                    </Stack>
                </Box>
            );
        }

        if (activeStep === 1) {
            return (
                <Box>
                    <Typography variant="subtitle1" sx={{ mb: 2 }}>
                        {objectType
                            ? `Wybierz obiekt (${PROCESS_TYPE_LABEL[objectType]})`
                            : "Wybierz obiekt"}
                    </Typography>

                    {objectType === "DEVICE" && (
                        <DeviceSelectField
                            value={deviceId}
                            onChange={(id) => setDeviceId(id)}
                            onSelectDevice={() => { }}
                        />
                    )}

                    {objectType === "COMPUTERIZED_SYSTEM" && (
                        <ComputerizedSystemSelectField
                            value={systemId}
                            onChange={(id) => setSystemId(id)}
                            onSelectSystem={() => { }}
                        />
                    )}

                    {objectType === "ROOM" && (
                        <TextField
                            label="Id / nazwa pomieszczenia"
                            fullWidth
                            value={roomId ?? ""}
                            onChange={(e) => setRoomId(e.target.value || null)}
                        />
                    )}

                    {serverError && (
                        <Alert
                            sx={{ mt: 2 }}
                            onClose={() => {
                                setServerError("");
                            }}
                            severity="error"
                        >
                            <Localized id={serverError}>
                                <span>Server error</span>
                            </Localized>
                        </Alert>
                    )}
                </Box>
            );
        }

        return null;
    };

    const handleDialogClose = (_e: object, reason: "backdropClick" | "escapeKeyDown") => {
        if (reason === "backdropClick") return;
        handleClose();
    };

    return (
        <Dialog open={open} onClose={handleDialogClose} maxWidth="md" fullWidth>
            <DialogTitle
                sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
            >
                Zmień walidowany obiekt
                <IconButton onClick={handleClose}>
                    <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Box display="flex">
                    <Box width={230} bgcolor={theme.palette.grey[100]} p={2}>
                        <Stepper activeStep={activeStep} orientation="vertical">
                            {steps.map((label, index) => (
                                <Step key={label} completed={index < activeStep}>
                                    <StepLabel>{label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    </Box>
                    <Box flexGrow={1} ml={2}>
                        {renderStepContent()}
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ bgcolor: theme.palette.grey[100] }}>
                <Button onClick={handleClose}>Anuluj</Button>
                <Button onClick={handleBack} disabled={activeStep === 0}>
                    Wstecz
                </Button>
                <Button
                    variant="contained"
                    onClick={activeStep === steps.length - 1 ? handleSave : handleNext}
                    disabled={saving || !canGoNext()}
                >
                    {activeStep === steps.length - 1 ? "Zapisz" : "Dalej"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
